/**
 * In-progress audits expire 72 hours after they were STARTED (owner
 * decision 2026-10-15: a fixed deadline, not reset by activity). An audit
 * submitted on the device before the deadline counts even if it only
 * syncs later. Expired audits are kept in SharePoint as 'expired' and
 * drop out of every list; tasks raised during them stay open.
 */

export const AUDIT_EXPIRY_HOURS = 72;

const HOUR_MS = 3_600_000;

/** Epoch ms when the audit expires, or null for an unreadable start date. */
export function auditExpiresAt(startedAt: string): number | null {
  const start = Date.parse(startedAt);
  return Number.isNaN(start) ? null : start + AUDIT_EXPIRY_HOURS * HOUR_MS;
}

/**
 * True once the 72 hours have passed. An unreadable start date never
 * expires — closing an audit (and discarding work) on bad data would be
 * the worse failure.
 */
export function isAuditExpired(startedAt: string, now: number = Date.now()): boolean {
  const at = auditExpiresAt(startedAt);
  return at !== null && now >= at;
}

/** Whole hours left before expiry (0 when expired); null if unknown. */
export function hoursUntilExpiry(startedAt: string, now: number = Date.now()): number | null {
  const at = auditExpiresAt(startedAt);
  if (at === null) return null;
  return Math.max(0, Math.floor((at - now) / HOUR_MS));
}

/**
 * The countdown wording used everywhere an in-progress audit is shown:
 * "53 hours until this audit expires" / "1 hour until…" /
 * "Less than an hour until this audit expires" / "This audit has expired".
 * Empty for an unreadable start date.
 */
export function expiryNotice(startedAt: string, now: number = Date.now()): string {
  const at = auditExpiresAt(startedAt);
  if (at === null) return '';
  if (now >= at) return 'This audit has expired';
  const hours = Math.floor((at - now) / HOUR_MS);
  if (hours < 1) return 'Less than an hour until this audit expires';
  return `${hours} hour${hours === 1 ? '' : 's'} until this audit expires`;
}

/** Warn on the conduct page when this few hours (or fewer) remain. */
export const EXPIRY_WARNING_HOURS = 12;
